<template>
  <v-dialog
    v-model="showDialog"
    max-width="550"
    persistent
    content-class="referral-info-popup"
  >
    <v-card class="info-card">
      <!-- Animated Background -->
      <div class="popup-background">
        <div class="bg-shape shape-1"></div>
        <div class="bg-shape shape-2"></div>
        <div class="bg-shape shape-3"></div>
      </div>

      <!-- Close Button -->
      <v-btn icon class="close-btn" @click="closePopup">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <!-- Header -->
      <div class="popup-header">
        <div class="header-icon">
          <v-icon size="40" color="white">mdi-account-group</v-icon>
        </div>
        <h2 class="popup-title">Earn Commissions 🎯</h2>
        <p class="popup-subtitle">Learn how our referral program works</p>
      </div>

      <!-- Explanatory Content -->
      <div class="info-section">
        <div class="info-block">
          <div class="info-icon"><v-icon color="#0C49C2">mdi-account-plus</v-icon></div>
          <div class="info-text">
            <h3>1. Refer your friends</h3>
            <p>Share your unique referral link. Anyone who signs up using your link becomes your referral.</p>
          </div>
        </div>
        <div class="info-block">
          <div class="info-icon"><v-icon color="#0C49C2">mdi-piggy-bank</v-icon></div>
          <div class="info-text">
            <h3>2. Your referrals activate a campaign</h3>
            <p>Your referral must have an <strong>active campaign</strong> with a minimum balance of <strong>2,000 FCFA</strong> for you to earn a commission.</p>
          </div>
        </div>
        <div class="info-block">
          <div class="info-icon"><v-icon color="#0C49C2">mdi-cash-multiple</v-icon></div>
          <div class="info-text">
            <h3>3. Earn 1,000 FCFA</h3>
            <p>You receive <strong>1,000 FCFA</strong> commission for each active referral. This amount appears in your "Commission" balance.</p>
          </div>
        </div>
        <div class="info-block">
          <div class="info-icon"><v-icon color="#0C49C2">mdi-swap-horizontal</v-icon></div>
          <div class="info-text">
            <h3>4. Transfer or withdraw</h3>
            <p>You can transfer your commissions to your main account or request a withdrawal anytime.</p>
          </div>
        </div>
      </div>

      <!-- Call to Action -->
      <div class="action-section">
        <v-btn class="cta-btn" large color="primary" block @click="closePopup">
          <v-icon left>mdi-check-circle</v-icon>
          Got it
        </v-btn>
      </div>

      <!-- Footer with Referral Link -->
      <div class="popup-footer">
        <p class="referral-link-label">Your referral link:</p>
        <div class="referral-link-box">
          <code class="referral-link">{{ truncatedReferralLink }}</code>
          <v-btn icon small @click="copyReferralLink" title="Copy link">
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </div>
        <div class="share-buttons-mini">
          <v-btn icon small @click="shareViaWhatsApp" title="Share on WhatsApp">
            <v-icon small>mdi-whatsapp</v-icon>
          </v-btn>
          <v-btn icon small @click="shareViaFacebook" title="Share on Facebook">
            <v-icon small>mdi-facebook</v-icon>
          </v-btn>
          <v-btn icon small @click="shareViaSMS" title="Share via SMS">
            <v-icon small>mdi-message</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';

export default {
  name: 'ReferralEarningsPopup',
  data() {
    return {
      showDialog: false,
      checkInterval: null,
      maxChecks: 10,
      checkCount: 0,
      encryptedReferralCode: '',
      hasBeenShown: false,
      isMarkedSeen: false
    };
  },
  computed: {
    ...mapGetters([
      'referralEarningsSeenForCurrentUser',
      'referralEarningsCurrentVersion'
    ]),
    currentUserId() {
      return this.$store.state.user_info?.user?.id || this.$store.state.username || 'anonymous';
    },
    referralLink() {
      const baseUrl = 'https://myapp.pettycashglobal.com';
      const code = this.encryptedReferralCode;
      return `${baseUrl}/create_business_account2?ref=${code}`;
    },
    truncatedReferralLink() {
      const link = this.referralLink;
      return link.length > 50 ? link.substring(0, 50) + '…' : link;
    },
    shouldShowPopup() {
      if (this.hasBeenShown) return false;
      return !this.referralEarningsSeenForCurrentUser;
    }
  },
  watch: {
    currentUserId: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal && newVal !== 'anonymous') {
          this.initEncryptedCode();
          if (!this.hasBeenShown) {
            this.checkAndShow();
          }
        }
      }
    }
  },
  mounted() {
    this.startChecking();
  },
  beforeDestroy() {
    this.stopChecking();
  },
  methods: {
    ...mapMutations(['setReferralEarningsSeenForUser']),

    encodeReferralCode(serialNo) {
      if (!serialNo) return '';
      try {
        const payload = `pc_ref:${serialNo}`;
        return btoa(payload)
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
      } catch (e) {
        console.error('Encode error:', e);
        return '';
      }
    },

    initEncryptedCode() {
      const serialNo = this.$store.state.user_info?.user_profile?.serial_no;
      if (serialNo) {
        this.encryptedReferralCode = this.encodeReferralCode(serialNo);
      } else {
        const unwatch = this.$watch(
          () => this.$store.state.user_info?.user_profile?.serial_no,
          (newVal) => {
            if (newVal) {
              this.encryptedReferralCode = this.encodeReferralCode(newVal);
              unwatch();
            }
          },
          { immediate: true }
        );
      }
    },

    startChecking() {
      this.checkAndShow();
      this.checkInterval = setInterval(() => {
        this.checkCount++;
        this.checkAndShow();
        if (this.showDialog || this.checkCount >= this.maxChecks || this.hasBeenShown) {
          this.stopChecking();
        }
      }, 500);
    },

    stopChecking() {
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    },

    checkAndShow() {
      if (this.shouldShowPopup && !this.showDialog && !this.hasBeenShown) {
        this.showDialog = true;
        this.hasBeenShown = true;
        this.stopChecking();
      }
    },

    closePopup() {
      this.markAsSeen();
      this.showDialog = false;
    },

    markAsSeen() {
      if (this.isMarkedSeen) return;
      this.isMarkedSeen = true;
      this.setReferralEarningsSeenForUser({ userId: this.currentUserId });
    },

    async copyReferralLink() {
      try {
        await navigator.clipboard.writeText(this.referralLink);
        this.$store.commit('setSnackBarMessage', 'Link copied to clipboard 📋');
        this.$store.commit('setSnackBarColor', 'success darken-2');
        this.$store.commit('activateSnackBar', true);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    },

    shareViaWhatsApp() {
      const link = this.referralLink;
      const serialNo = this.$store.state.user_info?.user_profile?.serial_no || '';
      const message = `🌟 Join PettyCash and earn commissions!\n\nUse my referral code *${serialNo}* to sign up:\n${link}\n\n_PettyCash — Fintech Made in Cameroon_ 🇨🇲`;
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    },

    shareViaFacebook() {
      const link = this.referralLink;
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`, '_blank', 'width=600,height=400');
    },

    shareViaSMS() {
      const link = this.referralLink;
      const serialNo = this.$store.state.user_info?.user_profile?.serial_no || '';
      const body = `Join PettyCash with my code ${serialNo}: ${link}`;
      window.open(`sms:?body=${encodeURIComponent(body)}`);
    }
  }
};
</script>

<style scoped>
.referral-info-popup { border-radius: 24px; overflow: hidden; }
.info-card { position: relative; background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%); border-radius: 24px; overflow: hidden; padding: 0; }
.popup-background { position: absolute; top: 0; left: 0; right: 0; bottom: 0; overflow: hidden; z-index: 0; }
.bg-shape { position: absolute; border-radius: 50%; opacity: 0.06; }
.shape-1 { width: 300px; height: 300px; background: linear-gradient(135deg, #0C49C2, #3A6BFF); top: -150px; right: -100px; animation: float 20s infinite linear; }
.shape-2 { width: 200px; height: 200px; background: linear-gradient(135deg, #FF6B35, #FF8E5E); bottom: -100px; left: -50px; animation: float 25s infinite linear reverse; }
.shape-3 { width: 150px; height: 150px; background: linear-gradient(135deg, #00C9A7, #00E3B8); top: 40%; left: 10%; animation: float 18s infinite linear; }
@keyframes float {
  0% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(15px, 15px) rotate(90deg); }
  50% { transform: translate(0, 30px) rotate(180deg); }
  75% { transform: translate(-15px, 15px) rotate(270deg); }
  100% { transform: translate(0, 0) rotate(360deg); }
}
.close-btn { position: absolute !important; top: 16px; right: 16px; z-index: 10; background: rgba(255, 255, 255, 0.9) !important; backdrop-filter: blur(10px); }
.popup-header { position: relative; z-index: 1; text-align: center; padding: 40px 30px 20px; }
.header-icon { width: 80px; height: 80px; border-radius: 50%; background: linear-gradient(135deg, #0C49C2, #3A6BFF); display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; box-shadow: 0 10px 30px rgba(12, 73, 194, 0.3); }
.popup-title { font-size: 28px; font-weight: 800; color: #1A1F36; margin-bottom: 8px; }
.popup-subtitle { font-size: 16px; color: #6C757D; margin: 0; }
.info-section { position: relative; z-index: 1; padding: 20px 30px; }
.info-block { display: flex; align-items: flex-start; gap: 16px; margin-bottom: 20px; }
.info-icon { width: 48px; height: 48px; border-radius: 14px; background: rgba(12, 73, 194, 0.08); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.info-text { flex: 1; }
.info-text h3 { font-size: 16px; font-weight: 700; color: #1A1F36; margin: 0 0 5px 0; }
.info-text p { font-size: 14px; color: #5a6570; margin: 0; line-height: 1.5; }
.info-text strong { color: #0C49C2; }
.action-section { position: relative; z-index: 1; padding: 10px 30px 20px; }
.cta-btn { border-radius: 12px !important; text-transform: none !important; font-weight: 600 !important; font-size: 16px !important; background: linear-gradient(135deg, #0C49C2, #3A6BFF) !important; box-shadow: 0 8px 20px rgba(12, 73, 194, 0.3) !important; }
.popup-footer { position: relative; z-index: 1; padding: 0 30px 30px; text-align: center; }
.referral-link-label { font-size: 14px; color: #6C757D; margin-bottom: 8px; }
.referral-link-box { display: flex; align-items: center; justify-content: center; gap: 8px; background: #f5f7fa; padding: 8px 16px; border-radius: 30px; margin-bottom: 10px; }
.referral-link { font-size: 13px; color: #0C49C2; background: transparent; padding: 4px 0; overflow: auto; white-space: nowrap; max-width: 280px; }
.share-buttons-mini { display: flex; justify-content: center; gap: 8px; }
.share-buttons-mini .v-btn { background: rgba(12, 73, 194, 0.1); }
@media (max-width: 480px) {
  .popup-title { font-size: 22px; }
  .header-icon { width: 60px; height: 60px; }
  .popup-header, .info-section, .action-section { padding-left: 20px; padding-right: 20px; }
  .referral-link { font-size: 11px; max-width: 200px; }
}
</style>